<template>
    <b-form class="mechanic-asset-receipt" @submit.stop.prevent="submit">
        <mechanic-asset-position-form-fields
            v-model="form"
            :accounting-type.sync="form.accounting_type"
            :accounting-type-options="accountingOptions"
            :location-type-options="locationTypeOptions"
            :warehouse-options="warehouseOptions"
            :warehouses-loading="warehousesLoading"
            :object-options="objectOptions"
            :objects-loading="objectsLoading"
            :object-error="objectErrorMessage"
            :user-options="userOptions"
            :meter-type-options="meterTypeOptions"
            :accounting-company-options="accountingCompanyOptions"
            :quantity-min="0.001"
            :show-status="false"
            :show-comment="false"
            :disabled="submitting"
            @accounting-type-change="onReceiptAccountingTypeChange"
        >
            <template #catalog>
                <b-form-group label="Группа справочника">
                    <b-form-radio-group
                        v-model="catalogMode"
                        :options="catalogModeOptions"
                        buttons
                        button-variant="outline-primary"
                        size="sm"
                        @change="onCatalogModeChange"
                    />
                </b-form-group>

                <template v-if="catalogMode === 'existing'">
                    <b-form-group label="Существующая группа" label-for="mechanic-receipt-catalog">
                        <v-select
                            id="mechanic-receipt-catalog"
                            v-model="selectedCatalog"
                            :options="catalogOptions"
                            :filterable="false"
                            :loading="catalogLoading"
                            label="text"
                            placeholder="Выберите группу или начните вводить"
                            @input="onExistingCatalogChange"
                            @search="searchCatalogs"
                        >
                            <template #no-options>
                                Группа не найдена
                            </template>
                        </v-select>
                        <small v-if="selectedCatalog" class="form-text text-muted">
                            {{ selectedCatalog.categoryLabel }}
                        </small>
                    </b-form-group>
                </template>

                <template v-else>
                    <b-form-group label="Название новой группы" label-for="mechanic-receipt-name">
                        <b-form-input
                            id="mechanic-receipt-name"
                            v-model.trim="form.name"
                            maxlength="500"
                            autocomplete="off"
                        />
                    </b-form-group>
                    <b-row>
                        <b-col md="4">
                            <b-form-group label="Категория" label-for="mechanic-receipt-category">
                                <b-form-select
                                    id="mechanic-receipt-category"
                                    v-model="form.category"
                                    :options="categoryOptions"
                                    @change="applyCategoryDefault"
                                />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Марка">
                                <b-form-input v-model.trim="form.brand" maxlength="255" />
                            </b-form-group>
                        </b-col>
                        <b-col md="4">
                            <b-form-group label="Модель">
                                <b-form-input v-model.trim="form.model" maxlength="255" />
                            </b-form-group>
                        </b-col>
                    </b-row>
                </template>
            </template>
        </mechanic-asset-position-form-fields>

        <b-row>
            <b-col md="4">
                <b-form-group label="Стоимость единицы" label-for="mechanic-receipt-cost">
                    <b-form-input
                        id="mechanic-receipt-cost"
                        v-model.number="form.unit_cost"
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="Не указана"
                    />
                </b-form-group>
            </b-col>
            <b-col md="2">
                <b-form-group label="Валюта" label-for="mechanic-receipt-currency">
                    <b-form-select
                        id="mechanic-receipt-currency"
                        v-model="form.currency"
                        :options="currencyOptions"
                    />
                </b-form-group>
            </b-col>
            <b-col md="6">
                <b-form-group label="Комментарий" label-for="mechanic-receipt-comment">
                    <AppTextarea
                        id="mechanic-receipt-comment"
                        v-model.trim="form.comment"
                        maxlength="1000"
                    />
                </b-form-group>
            </b-col>
        </b-row>

        <b-alert v-if="errorMessage" show variant="danger" class="mb-3">
            {{ errorMessage }}
        </b-alert>

        <div class="mechanic-asset-receipt__actions">
            <b-button type="button" variant="outline-secondary" :disabled="submitting" @click="$emit('cancel')">
                <i class="fas fa-arrow-left mr-1"></i>
                Назад к выбору
            </b-button>
            <b-button type="submit" variant="success" :disabled="!canSubmit || submitting">
                <b-spinner v-if="submitting" small class="mr-1" />
                <i v-else class="fas fa-box-open mr-1"></i>
                Оприходовать и выбрать
            </b-button>
        </div>
    </b-form>
</template>

<script>
import api from '@/utils/api';
import MechanicAssetPositionFormFields from '@/views/MechanicAssets/components/MechanicAssetPositionFormFields.vue';
import {
  MECHANIC_ASSET_ACCOUNTING_TYPE_LABELS,
  MECHANIC_ASSET_CATALOG_CATEGORY_LABELS
} from '@/utils/mechanicAssets';

const DEFAULT_ACCOUNTING_BY_CATEGORY = Object.freeze({
  machinery: 'inventory',
  vehicle: 'inventory',
  mechanism: 'inventory',
  equipment: 'inventory',
  tool: 'inventory',
  spare_part: 'quantity',
  consumable: 'quantity',
  inventory: 'inventory',
  other: 'quantity'
});

export default {
  name: 'MechanicAssetReceiptForm',
  components: {
    MechanicAssetPositionFormFields
  },
  props: {
    requestItemId: {
      type: [Number, String],
      required: true
    },
    requestedName: {
      type: String,
      default: ''
    },
    requestedQuantity: {
      type: [Number, String],
      default: 1
    },
    requestedUnit: {
      type: String,
      default: 'шт'
    }
  },
  data() {
    return {
      catalogMode: 'existing',
      selectedCatalog: null,
      catalogOptions: [],
      catalogLoading: false,
      catalogLoadSeq: 0,
      catalogSearchTimer: null,
      warehousesLoading: false,
      warehouseOptions: [],
      objectsLoading: false,
      objectOptions: [],
      objectErrorMessage: '',
      accountingCompanyOptions: [],
      submitting: false,
      errorMessage: '',
      form: {
        name: '',
        display_name: '',
        category: 'consumable',
        brand: '',
        model: '',
        accounting_type: 'quantity',
        unit: 'шт',
        location_type: 'warehouse',
        warehouse_id: null,
        building_process_id: null,
        responsible_user_id: null,
        quantity: 1,
        inventory_number: '',
        serial_number: '',
        plate_number: '',
        current_meter_type: 'moto_hour',
        current_meter_value: null,
        technical_condition: '',
        accounting_company_name: '',
        unit_cost: '',
        currency: 'BYN',
        comment: ''
      }
    };
  },
  computed: {
    catalogModeOptions() {
      return [
        {value: 'existing', text: 'Существующая группа'},
        {value: 'new', text: 'Новая группа'}
      ];
    },
    categoryOptions() {
      return Object.keys(MECHANIC_ASSET_CATALOG_CATEGORY_LABELS).map(value => ({
        value,
        text: MECHANIC_ASSET_CATALOG_CATEGORY_LABELS[value]
      }));
    },
    accountingOptions() {
      return ['inventory', 'quantity'].map(value => ({
        value,
        text: MECHANIC_ASSET_ACCOUNTING_TYPE_LABELS[value]
      }));
    },
    currencyOptions() {
      return ['BYN', 'RUB'].map(value => ({value, text: value}));
    },
    meterTypeOptions() {
      return [
        {value: null, text: 'Не указан'},
        {value: 'km', text: 'Километры'},
        {value: 'moto_hour', text: 'Моточасы'}
      ];
    },
    userOptions() {
      const users = this.$store && this.$store.state
          ? (this.$store.state.users || [])
          : [];
      return users
          .filter(user => user && user.active && !user.isBot)
          .map(user => ({
            value: Number(user.user_id),
            text: [user.last_name, user.first_name].filter(Boolean).join(' ').trim()
          }))
          .filter(option => Number.isInteger(option.value) && option.value > 0 && option.text)
          .sort((a, b) => a.text.localeCompare(b.text, 'ru'));
    },
    locationTypeOptions() {
      return [
        {value: 'warehouse', text: 'Склад / ремзона'},
        {value: 'object', text: 'Объект'}
      ];
    },
    selectedAccountingType() {
      return this.form.accounting_type;
    },
    isQuantityAccounting() {
      return this.selectedAccountingType === 'quantity';
    },
    canSubmit() {
      const hasCatalog = this.catalogMode === 'existing'
          ? !!(this.selectedCatalog && this.selectedCatalog.value)
          : !!String(this.form.name || '').trim();
      const hasLocation = this.form.location_type === 'object'
          ? !!this.form.building_process_id
          : !!this.form.warehouse_id;
      const quantity = Number(this.form.quantity);
      return hasCatalog &&
          !!String(this.form.display_name || '').trim() &&
          hasLocation &&
          (!this.isQuantityAccounting || (
            Number.isFinite(quantity) &&
            quantity > 0 &&
            !!String(this.form.unit || '').trim()
          ));
    }
  },
  created() {
    const requestedQuantity = Number(this.requestedQuantity);
    this.form.quantity = Number.isFinite(requestedQuantity) && requestedQuantity > 0
        ? requestedQuantity
        : 1;
    this.form.unit = String(this.requestedUnit || '').trim() || 'шт';
    this.form.name = String(this.requestedName || '').trim();
    this.form.display_name = String(this.requestedName || '').trim();
    const currentUser = this.$store && this.$store.getters
        ? this.$store.getters.currentUser
        : null;
    this.form.currency = currentUser && currentUser.company === 'aerodorstroy_ru' ? 'RUB' : 'BYN';
    this.loadWarehouses();
    this.loadObjects();
    this.loadCatalogs('');
    this.loadPositionFieldOptions();
  },
  beforeDestroy() {
    if (this.catalogSearchTimer) {
      clearTimeout(this.catalogSearchTimer);
    }
  },
  methods: {
    clearError() {
      this.errorMessage = '';
    },
    onCatalogModeChange(mode) {
      if (mode === 'existing') {
        this.loadCatalogs('');
      }
      this.clearError();
    },
    onReceiptAccountingTypeChange(accountingType) {
      this.onAccountingTypeChange(accountingType);
      this.clearError();
    },
    onExistingCatalogChange() {
      this.clearError();
    },
    normalizeCatalog(catalog) {
      const id = Number(catalog && catalog.id);
      if (!Number.isInteger(id) || id <= 0) {
        return null;
      }
      return {
        value: id,
        text: catalog.name || `Группа #${id}`,
        name: catalog.name || `Группа #${id}`,
        category: catalog.category || 'other',
        categoryLabel: MECHANIC_ASSET_CATALOG_CATEGORY_LABELS[catalog.category] || catalog.category || 'Прочее',
        is_active: catalog.is_active !== false
      };
    },
    searchCatalogs(search, toggleLoading) {
      if (this.catalogSearchTimer) {
        clearTimeout(this.catalogSearchTimer);
      }
      this.catalogSearchTimer = setTimeout(() => {
        this.loadCatalogs(search, toggleLoading);
      }, 250);
    },
    async loadCatalogs(search, toggleLoading) {
      const seq = this.catalogLoadSeq + 1;
      this.catalogLoadSeq = seq;
      this.catalogLoading = true;
      if (typeof toggleLoading === 'function') {
        toggleLoading(true);
      }
      try {
        const response = await api.get('/api/mechanic-assets/catalog', {
          params: {
            active: true,
            q: String(search || '').trim(),
            limit: 50
          }
        });
        if (seq !== this.catalogLoadSeq) {
          return;
        }
        const rows = response && response.data && Array.isArray(response.data.rows)
            ? response.data.rows
            : [];
        this.catalogOptions = rows
            .map(this.normalizeCatalog)
            .filter(Boolean);
      } catch (error) {
        if (seq === this.catalogLoadSeq) {
          this.errorMessage = 'Не удалось загрузить группы справочника';
        }
      } finally {
        if (seq === this.catalogLoadSeq) {
          this.catalogLoading = false;
        }
        if (typeof toggleLoading === 'function') {
          toggleLoading(false);
        }
      }
    },
    async loadWarehouses() {
      this.warehousesLoading = true;
      try {
        const response = await api.get('/api/mechanic-assets/warehouses', {
          params: {active: true}
        });
        const rows = response && Array.isArray(response.data) ? response.data : [];
        this.warehouseOptions = rows.map(row => ({
          value: Number(row.id),
          text: row.name || `Склад #${row.id}`
        })).filter(option => Number.isInteger(option.value) && option.value > 0);
        if (this.warehouseOptions.length === 1) {
          this.form.warehouse_id = this.warehouseOptions[0].value;
        }
      } catch (error) {
        this.errorMessage = 'Не удалось загрузить склады и ремзоны';
      } finally {
        this.warehousesLoading = false;
      }
    },
    async loadPositionFieldOptions() {
      try {
        const response = await api.get('/api/mechanic-assets/position-field-options');
        const data = response && response.data ? response.data : {};
        this.accountingCompanyOptions = Array.isArray(data.accounting_company_names)
            ? data.accounting_company_names
            : [];
      } catch (error) {
        this.accountingCompanyOptions = [];
      }
    },
    async loadObjects() {
      this.objectsLoading = true;
      this.objectErrorMessage = '';
      try {
        const request = this.$store.state.requestCard && this.$store.state.requestCard.request;
        const requestId = Number(request && request.id);
        if (!Number.isInteger(requestId) || requestId <= 0) {
          throw new Error('Заявка не загружена');
        }
        const response = await api.get(`/api/requests/${requestId}/mechanic-asset-target-object-options`, {
          params: {purpose: 'receipt'}
        });
        const processes = response && Array.isArray(response.data) ? response.data : [];
        this.objectOptions = (Array.isArray(processes) ? processes : []).map(process => {
          const value = Number(process && (process.value || process.building_process_id || process.id));
          if (!Number.isInteger(value) || value <= 0) {
            return null;
          }
          return {
            value,
            text: process.text || process.object_name || process.name || `Объект #${value}`
          };
        }).filter(Boolean);

        const requestObjectId = Number(request && request.building_process_id);
        if (Number.isInteger(requestObjectId) &&
            this.objectOptions.some(option => option.value === requestObjectId)) {
          this.form.building_process_id = requestObjectId;
        } else if (this.objectOptions.length === 1) {
          this.form.building_process_id = this.objectOptions[0].value;
        }
      } catch (error) {
        this.objectErrorMessage = 'Не удалось загрузить доступные объекты';
      } finally {
        this.objectsLoading = false;
      }
    },
    applyCategoryDefault(category) {
      const accountingType = DEFAULT_ACCOUNTING_BY_CATEGORY[category] || 'quantity';
      this.form.accounting_type = accountingType;
      this.onReceiptAccountingTypeChange(accountingType);
    },
    onAccountingTypeChange(accountingType) {
      if (accountingType === 'inventory') {
        this.form.quantity = 1;
        this.form.unit = 'шт';
        return;
      }
      this.form.inventory_number = '';
      this.form.serial_number = '';
      this.form.plate_number = '';
      this.form.current_meter_type = null;
      this.form.current_meter_value = null;
      if (!String(this.form.unit || '').trim()) {
        this.form.unit = 'шт';
      }
    },
    buildPayload() {
      const payload = {
        display_name: this.form.display_name,
        accounting_type: this.form.accounting_type,
        unit: this.form.unit,
        location_type: this.form.location_type,
        warehouse_id: this.form.location_type === 'warehouse' ? this.form.warehouse_id : null,
        building_process_id: this.form.location_type === 'object' ? this.form.building_process_id : null,
        responsible_user_id: this.form.responsible_user_id,
        quantity: this.isQuantityAccounting ? Number(this.form.quantity) : 1,
        inventory_number: this.isQuantityAccounting ? null : this.form.inventory_number,
        serial_number: this.isQuantityAccounting ? null : this.form.serial_number,
        plate_number: this.isQuantityAccounting ? null : this.form.plate_number,
        current_meter_type: this.isQuantityAccounting ? null : this.form.current_meter_type,
        current_meter_value: this.isQuantityAccounting ? null : this.form.current_meter_value,
        technical_condition: this.form.technical_condition,
        accounting_company_name: this.form.accounting_company_name,
        unit_cost: this.form.unit_cost === '' || this.form.unit_cost === null
            ? null
            : Number(this.form.unit_cost),
        currency: this.form.currency,
        comment: this.form.comment
      };
      if (this.catalogMode === 'existing') {
        payload.catalog_id = this.selectedCatalog.value;
      } else {
        payload.catalog = {
          name: this.form.name,
          category: this.form.category,
          brand: this.form.brand,
          model: this.form.model
        };
      }
      return payload;
    },
    getErrorData(error) {
      return error && error.response && error.response.data
          ? error.response.data
          : {};
    },
    async submit() {
      if (!this.canSubmit || this.submitting) {
        return;
      }
      this.submitting = true;
      this.errorMessage = '';
      try {
        const result = await this.$store.dispatch('requestCard/requestCard_receiveMechanicAsset', {
          itemId: Number(this.requestItemId),
          payload: this.buildPayload()
        });
        if (!result || !result.asset) {
          throw new Error('Сервер не вернул оприходованную позицию');
        }
        this.$emit('received', result.asset, result);
      } catch (error) {
        const data = this.getErrorData(error);
        if (data.code === 'MECHANIC_CATALOG_DUPLICATE' && data.details && data.details.catalog) {
          const duplicate = this.normalizeCatalog(data.details.catalog);
          if (duplicate && duplicate.is_active) {
            this.catalogOptions = [duplicate].concat(
                this.catalogOptions.filter(option => Number(option.value) !== Number(duplicate.value))
            );
            this.selectedCatalog = duplicate;
            this.catalogMode = 'existing';
            this.onExistingCatalogChange(duplicate);
          }
        }
        this.errorMessage = data.message || error.message || 'Не удалось оприходовать позицию';
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.mechanic-asset-receipt {
    padding: 14px 16px 16px;
}

.mechanic-asset-receipt__actions {
    display: flex;
    justify-content: space-between;
    gap: 10px;
    padding-top: 4px;
    border-top: 1px solid #dee2e6;
}

@media (max-width: 575.98px) {
    .mechanic-asset-receipt {
        padding: 12px;
    }

    .mechanic-asset-receipt__actions {
        flex-direction: column-reverse;
    }

    .mechanic-asset-receipt__actions .btn {
        width: 100%;
    }
}
</style>
